<script setup lang="ts">
import { ref, watch } from 'vue';
import Modal from "@/components/Modal.vue";
import Button from "@/components/Button.vue";

interface Props {
  isOpen: boolean;
  imageUrl: string;
  title?: string;
  isLoading?: boolean; 
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Image Preview',
  isLoading: false
});

const emit = defineEmits(['close']);

const isDecoding = ref(true);

watch(() => props.imageUrl, () => {
  if (props.imageUrl) {
    isDecoding.value = true;
  }
});

const handleImageLoaded = () => {
  isDecoding.value = false;
};
</script>

<template>
  <Modal
    :is-open="isOpen"
    :title="title"
    size="md"
    @close="emit('close')"
  >
    <div class="flex flex-col items-center justify-center min-h-[300px]">
      
      <div
        v-if="isLoading || (imageUrl && isDecoding)"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80"
      >
        <div class="h-10 w-10 animate-spin rounded-full border-8 border-ram-red border-t-transparent"></div>
        <p class="mt-2 text-xs font-medium text-gray-500">Rendering Image...</p>
      </div>

      <div
        class="overflow-hidden rounded-lg border border-gray-200 bg-gray-100 w-full flex justify-center"
      >
        <img
          v-if="imageUrl"
          :src="imageUrl"
          alt="Preview"
          @load="handleImageLoaded"
          class="max-h-[70vh] w-full object-contain transition-opacity duration-300"
          :class="isDecoding ? 'opacity-0' : 'opacity-100'"
        />
        <div v-else-if="!isLoading" class="p-8 text-gray-500">
          No image available to display.
        </div>
      </div>
    </div>

    <template #footer>
      <Button variant="ram" @click="emit('close')">
        Close Preview
      </Button>
    </template>
  </Modal>
</template>